import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

// Full (up to 500), unpaginated list of an org's own IT components — powers
// searchable dropdowns (e.g. attaching a component as a dependency) and the
// IT Components page's own category filter (which categories are actually
// present among this org's components, and how many of each — derived from
// this list rather than a dedicated facet endpoint). Deliberately separate
// from orgComponents.js, which is a paginated store scoped to that page's
// own filters/sort state.
export const useOrgComponentOptionsStore = defineStore('orgComponentOptions', {
  state: () => ({
    organizationId: null,
    components: [],
    loading: false,
    fetchedAt: null,

    // A second, separately cached list spanning both context types
    // (ItComponent and Application) — used only by the Dependencies
    // attach-form picker, since an application can depend on another of the
    // org's own tracked applications, not just a plain IT component. Kept
    // apart from `components` above so it never leaks Application rows into
    // the IT Components page's own category filter or catalog dedup checks.
    allComponents: [],
    allLoading: false,
    allFetchedAt: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,

    isAllCacheValid: (state) =>
      state.allFetchedAt !== null && Date.now() - state.allFetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfNeeded(organizationId) {
      if (this.organizationId === organizationId && this.isCacheValid) return

      this.organizationId = organizationId
      this.loading = true
      try {
        const { axiosRequest } = useRequest()
        const query = 'sort_by=name&sort_order=asc&page[number]=1&page[limit]=500'
        const res = await axiosRequest('GET', `/api/v1/organizations/${organizationId}/org_components?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.components = res.response.data.records
          this.fetchedAt = Date.now()
        }
      } finally {
        this.loading = false
      }
    },

    async fetchAllIfNeeded(organizationId) {
      if (this.organizationId === organizationId && this.isAllCacheValid) return

      this.organizationId = organizationId
      this.allLoading = true
      try {
        const { axiosRequest } = useRequest()
        const query = 'sort_by=name&sort_order=asc&page[number]=1&page[limit]=500&context_type[]=ItComponent&context_type[]=Application'
        const res = await axiosRequest('GET', `/api/v1/organizations/${organizationId}/org_components?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.allComponents = res.response.data.records
          this.allFetchedAt = Date.now()
        }
      } finally {
        this.allLoading = false
      }
    },

    // Call after a component is added/edited/removed elsewhere (e.g. the IT
    // Components page's own mutations) so the category filter's counts and
    // available options don't sit stale for the rest of the TTL window.
    invalidate() {
      this.fetchedAt = null
      this.allFetchedAt = null
    },
  },
})
